import { CheckRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Button from "../../../components/ui/Button";
import Chip from "../../../components/ui/Chip";
import Modal from "../../../components/ui/Modal";
import SegmentedControl from "../../../components/ui/SegmentedControl";
import {
  ChineseCharacterMode,
  useReadingSettings,
} from "../../common/util/chineseCharacterMode";
import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import { QuestionType } from "../../settings/model/QuestionType";
import { SortType } from "../../settings/model/SortType";
import { TermLevel } from "../custom_view/TermLevel";
import styles from "../css/TermListFilterSheet.module.css";

interface TermListFilterSheetProps {
  open: boolean;
  onClose: () => void;
  resultCount: number;
  selectedLevels: TermLevel[];
  onToggleLevel: (checked: boolean, level: TermLevel) => void;
  sortType: SortType;
  onChangeSortType: (sortType: SortType) => void;
  questionType: QuestionType;
  onChangeQuestionType: (questionType: QuestionType) => void;
  pronPosition: PronunciationPosition;
  onChangePronPosition: (position: PronunciationPosition) => void;
  showChineseCharacterMode: boolean;
}

// 단어장 필터 · 보기 설정. 모바일은 바텀시트, 데스크톱은 다이얼로그로 뜬다.
// 바꾸는 즉시 목록에 반영된다.
export default function TermListFilterSheet(props: TermListFilterSheetProps) {
  const { t } = useTranslation();
  const { chineseCharacterMode, setChineseCharacterMode } =
    useReadingSettings();

  const levels = [
    { level: TermLevel.difficult, label: t("Difficult"), tone: styles.hard },
    { level: TermLevel.familiar, label: t("Familiar"), tone: styles.mid },
    { level: TermLevel.perfect, label: t("Perfect"), tone: styles.done },
  ];

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={t("TermListFilterTitle")}
      showCloseButton
      closeLabel={t("CloseButton")}
      footer={
        <Button size="lg" fullWidth onClick={props.onClose}>
          {t("GroupSelectTermCount", { termCount: props.resultCount })}
        </Button>
      }
    >
      <div className={styles.sections}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t("TermListFilterLevelTitle")}
          </h3>
          <div className={styles.levelChips}>
            {levels.map((item) => {
              const selected = props.selectedLevels.includes(item.level);
              // 최소 하나의 레벨은 선택되어 있어야 한다
              const isLastSelected =
                selected && props.selectedLevels.length === 1;
              return (
                <Chip
                  key={item.level}
                  selected={selected}
                  selectedStyle="outline"
                  className={selected ? item.tone : undefined}
                  disabled={isLastSelected}
                  leadingIcon={selected ? <CheckRounded /> : undefined}
                  onClick={() => props.onToggleLevel(!selected, item.level)}
                >
                  {item.label}
                </Chip>
              );
            })}
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t("Sort")}</h3>
          <SegmentedControl
            aria-label={t("Sort")}
            value={props.sortType}
            onChange={props.onChangeSortType}
            options={[
              { value: SortType.recent, label: t("Latest") },
              { value: SortType.old, label: t("Old") },
              { value: SortType.abc, label: t("Alphabet") },
            ]}
          />
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t("TermListSettingCardStyle")}
          </h3>
          <SegmentedControl
            aria-label={t("TermListSettingCardStyle")}
            value={props.questionType}
            onChange={props.onChangeQuestionType}
            options={[
              {
                value: QuestionType.showAll,
                label: t("TermListSettingCardStyleShowAll"),
              },
              {
                value: QuestionType.termFirst,
                label: t("TermListSettingCardStyleHideDefinition"),
              },
              {
                value: QuestionType.definitionFirst,
                label: t("TermListSettingCardStyleHideTerm"),
              },
            ]}
          />
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t("PronPosition")}</h3>
          <SegmentedControl
            aria-label={t("PronPosition")}
            value={props.pronPosition}
            onChange={props.onChangePronPosition}
            options={[
              {
                value: PronunciationPosition.onTerm,
                label: t("PronOnQuestion"),
              },
              {
                value: PronunciationPosition.onDefinition,
                label: t("PronOnAnswer"),
              },
            ]}
          />
        </section>

        {props.showChineseCharacterMode && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>
              {t("ChineseCharacterModeSettingTitle")}
            </h3>
            <SegmentedControl<ChineseCharacterMode>
              aria-label={t("ChineseCharacterModeSettingTitle")}
              value={chineseCharacterMode}
              onChange={setChineseCharacterMode}
              options={[
                {
                  value: "default",
                  label: t("ChineseCharacterModeSettingNomral"),
                },
                {
                  value: "japanese",
                  label: t("ChineseCharacterModeSettingJapanese"),
                },
              ]}
            />
          </section>
        )}
      </div>
    </Modal>
  );
}
